import { AssessmentModule, CreateModuleInput } from '@amzn/aws-assessment-template-management-service-typescript-client';
import { Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { useMutation } from '@apollo/client';
import dompurify from 'dompurify';
import { FunctionComponent, useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import templateManagementClient from '../../../../../api/templateManagement/TemplateManagementClient';
import { CREATE_MODULE } from '../../../../../api/templateManagement/TemplateManagementMutations';
import { AppLabelsContextInterface, withAppLabelsContext } from '../../../../../common/AppLabelsContext';
import rumClient from '../../../../../common/monitoring/RumClient';
import { createTemplateModule, removeInProgressRequest } from '../../../../administration/manage-templates/edit-template/CurrentTemplateSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';

type AddSectionModalProps = AppLabelsContextInterface & {
    visible: boolean;
    setVisible: (visible: boolean) => void;
};

const AddSectionModal: FunctionComponent<AddSectionModalProps> = ({ appLabels, visible, setVisible }) => {
    const dispatch = useAppDispatch();

    const currentViewId = useAppSelector((state) => state.currentTemplateState.currentPromptState.currentViewId);

    const [createSectionMutation, { loading }] = useMutation(CREATE_MODULE, {
        client: templateManagementClient,
        onError: (error, options) => {
            rumClient.recordError(error);
            dispatch(removeInProgressRequest({ requestId: options.context.requestId, didRequestFail: true }));
        },
        onCompleted: (data, options) => {
            const newSection: AssessmentModule = data.createModule;
            dispatch(createTemplateModule({ viewId: currentViewId, containingModuleId: undefined, newModule: newSection }));
            dispatch(removeInProgressRequest({ requestId: options.context.requestId }));
        },
    });

    const onConfirm = useCallback(async () => {
        try {
            const addSectionInput: CreateModuleInput = {
                parentViewId: currentViewId,
            };

            const requestId = uuid();
            await createSectionMutation({ variables: { input: addSectionInput }, context: { requestId } });
        } finally {
            setVisible(false);
        }
    }, [currentViewId, createSectionMutation, setVisible]);

    // Render HTML app label
    const addSectionModalContent = dompurify.sanitize(appLabels.manage_templates.edit.modify_structure.add_section_modal_content);

    return (
        <Modal
            data-testid='add-section-modal'
            onDismiss={() => setVisible(false)}
            footer={
                <Box float='right'>
                    <SpaceBetween direction='horizontal' size='xs'>
                        <Button variant='link' disabled={loading} onClick={() => setVisible(false)}>
                            {appLabels.user_actions.cancel}
                        </Button>
                        <Button variant='primary' loading={loading} onClick={onConfirm} data-testid='btn-confirm-add-section'>
                            {appLabels.user_actions.confirm}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
            header={appLabels.manage_templates.edit.modify_structure.add_section}
            visible={visible}
        >
            <div dangerouslySetInnerHTML={{ __html: addSectionModalContent }} />
        </Modal>
    );
};

export default withAppLabelsContext(AddSectionModal);
